import { memo, ReactNode, useState } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import { Link } from "react-router-dom";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { useHelpAssetUrl } from "../../hooks/useHelpCentre";
import { helpAssetIdOf } from "../../utils/helpCentre/content";

/** GitHub-style tables and lists, and a line break where the author put one. */
const REMARK_PLUGINS = [remarkGfm, remarkBreaks];

/**
 * A path on Terra itself: one slash, not followed by another or by a
 * backslash, which browsers read as the start of another site's address.
 * react-markdown already percent-encodes a backslash in an address, so the
 * backslash case is a second line of defence.
 */
const isTerraPath = (address: string) => /^\/(?![/\\])/.test(address);

/**
 * A link in help content: a Terra path through the router, so the page
 * doesn't reload; a web address in a new tab that can't reach back into
 * Terra; anything else as written. react-markdown replaces an unsafe address
 * (`javascript:`, `data:` and the like) with `javascript:void(0)`, which is
 * shown as plain text.
 */
function MarkdownLink({
	href = "",
	children,
}: Readonly<{
	href?: string;
	children?: ReactNode;
}>) {
	if (!href || /^javascript:/i.test(href)) return <>{children}</>;
	if (isTerraPath(href)) {
		return <Link to={href}>{children}</Link>;
	}
	// So does `//` (and `/\`, see isTerraPath).
	if (/^https?:/i.test(href) || /^\/[/\\]/.test(href)) {
		return (
			<a href={href} target="_blank" rel="noopener noreferrer">
				{children}
				<span className="sr-only"> (opens in a new tab)</span>
			</a>
		);
	}
	return <a href={href}>{children}</a>;
}

/**
 * An uploaded image, shown by the link the server signed for it. A link that
 * fails to load (it may have expired) has the links fetched again; a new
 * link is shown, and the same one is tried once more. If that fails too, the
 * image shows its alt text, as one that can't be found does.
 */
function HelpAssetImage({ id, alt }: Readonly<{ id: string; alt: string }>) {
	const { url, isPending, isFetching, refresh } = useHelpAssetUrl(id);
	// The link that failed, and whether it has had its second try.
	const [failure, setFailure] = useState<{ url: string; retried: boolean }>();
	const failed = !!url && failure?.url === url;
	const retrying = failed && !failure?.retried && !isFetching;

	if (url && (!failed || retrying)) {
		return (
			<img
				// A new element, so the browser loads the link again.
				key={retrying ? "retry" : "first"}
				src={url}
				alt={alt}
				loading="lazy"
				onError={() => {
					setFailure({ url, retried: failed });
					if (!failed) refresh();
				}}
			/>
		);
	}
	if (isPending || (failed && isFetching)) {
		return (
			<span
				className="help-markdown__image-pending"
				role="img"
				aria-label={alt}
				aria-busy="true"
			/>
		);
	}
	return <>{alt}</>;
}

/**
 * Images are uploads (`help-asset:<id>`) or Terra's own paths. react-markdown
 * checks link addresses but not image sources, so this does it: anything
 * else, other websites included, shows its alt text.
 */
function HelpImage({
	src,
	alt = "",
}: Readonly<{ src?: string; alt?: string }>) {
	const assetId = helpAssetIdOf(src);
	if (assetId) return <HelpAssetImage id={assetId} alt={alt} />;
	return src && isTerraPath(src) ? (
		<img src={src} alt={alt} loading="lazy" />
	) : (
		<>{alt}</>
	);
}

/** Content headings start at h3: the page has the h1 and the section title the h2. */
const COMPONENTS: Components = {
	img: ({ src, alt }) => <HelpImage src={src} alt={alt} />,
	a: ({ href, children }) => (
		<MarkdownLink href={href}>{children}</MarkdownLink>
	),
	h1: ({ children }) => <h3>{children}</h3>,
	h2: ({ children }) => <h3>{children}</h3>,
	h3: ({ children }) => <h4>{children}</h4>,
	h4: ({ children }) => <h5>{children}</h5>,
	h5: ({ children }) => <h6>{children}</h6>,
	h6: ({ children }) => <h6>{children}</h6>,
	// Wide tables scroll inside the article instead of widening the page.
	table: ({ children }) => (
		<div className="help-markdown__table">
			<table>{children}</table>
		</div>
	),
};

/**
 * Help Centre markdown, with GitHub-style tables and lists, and a single line
 * break shown as one (remark-breaks), as authors expect from the editor. Raw
 * HTML stays escaped text (no rehype-raw): super-admins write this content,
 * but it is shown to every user. Memoised, as answers re-render with their
 * list.
 */
const HelpMarkdown = memo(function HelpMarkdown({
	markdown,
}: Readonly<{
	markdown: string;
}>) {
	return (
		<div className="help-markdown">
			<ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
				{markdown}
			</ReactMarkdown>
		</div>
	);
});

export default HelpMarkdown;
